import React, { useCallback, useState } from "react";
import { cloneDeep, get } from "lodash";
import { useFieldArray } from "react-hook-form";
import useTranslations from "../../hooks/useTranslations";
import { Input } from "../wombat-ui";
import AddTemplateElementButton from "./AddTemplateElementButton";
import {
  SectionForm,
  SectionFormInput,
} from "./SectionForm";

import { Button, MenuButton, IconButton } from "@chakra-ui/react";
import { PlusIcon } from "../icons";

const TemplateForm = ({
  register,
  unregister,
  setValue,
  trigger,
  getValues,
  errors,
  control,
  reset,
  name,
}) => {
  const [focusedIndex, setFocusedIndex] = useState<string>(null);
  const { fields, append, insert } = useFieldArray<
    SectionFormInput,
    "key"
  >({
    control,
    name: `${name}.sectionsAttributes`,
    keyName: "key",
  });

  const t = useTranslations();

  const handleFocus = useCallback(
    (sectionIndex: number, questionIndex: number) => {
      setFocusedIndex(`${sectionIndex}-${questionIndex}`);
    },
    []
  );

  const handleRemoveSection = useCallback(
    (index) => {
      // clone it to make mutating the value easier
      const values = cloneDeep(getValues());
      get(values, name).sectionsAttributes.splice(index, 1);

      reset(values);
    },
    [name]
  );

  const handleQuestionBlur = useCallback(() => {
    setFocusedIndex(null);
  }, [setFocusedIndex]);

  return (
    <>
      <div className="flex flex-col">
        <div className="pt-3">
          <Input
            id="title"
            ref={register({ required: t("templates.titleRequired") as string })}
            name={`${name}.title`}
            label={t("templates.title") as string}
            error={errors?.title}
          />
        </div>
      </div>
      {fields.map((field, index) => {
        return (
          <div key={field.key}>
            <div className="relative flex justify-center sm:mx-8 group">
              <div className="absolute w-full h-px inset-y-1/2 bg-light-100 group-hover:bg-light-500" />
              <AddTemplateElementButton
                insideSection={false}
                handleFocus={handleFocus}
                setValue={setValue}
                getValues={getValues}
                fields={fields}
                append={append}
                insert={insert}
                reset={reset}
                name={name}
                insertIndex={index}
              >
                <MenuButton
                  as={IconButton}
                  aria-label="Add form element"
                  icon={<PlusIcon className="w-4 h-4 text-dark-100" />}
                  data-test-id={`insert_section_${index}`}
                />
              </AddTemplateElementButton>
            </div>
            <SectionForm
              sectionDisplayIndex={index}
              index={index}
              control={control}
              register={register}
              unregister={unregister}
              trigger={trigger}
              setValue={setValue}
              getValues={getValues}
              errors={errors?.sectionsAttributes?.[index]}
              defaultValue={field}
              focusedIndex={focusedIndex}
              onBlur={handleQuestionBlur}
              handleFocus={handleFocus}
              onRemove={handleRemoveSection}
              reset={reset}
              name={`${name}.sectionsAttributes[${index}]`}
            />
          </div>
        );
      })}
      <AddTemplateElementButton
        insideSection={false}
        handleFocus={handleFocus}
        setValue={setValue}
        getValues={getValues}
        fields={fields}
        append={append}
        insert={insert}
        reset={reset}
        name={name}
        insertIndex={-1}
      >
        <MenuButton
          as={Button}
          aria-label="Add form element"
          data-test-id={`${name}.addElementButton`}
        >
          {t("templates.createElement")}
        </MenuButton>
      </AddTemplateElementButton>
    </>
  );
};

export default React.memo(TemplateForm);
